import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { os } from '@ces/observable-state';
import { DialogNavigations } from '@egovsolutions/angular-dialog-navigations';
import { CardPaymentMethod, EcheckPaymentMethod, PaymentMethod } from '@egovsolutions/angular-payment-methods';
import { CardProvider } from '@egovsolutions/angular-payment-types';
import { ScrollableModal } from '@egovsolutions/angular-scrollable-modal';
import { Store } from '@ngrx/store';
import { NewPaymentMethodComponent } from '@payment/components/dialogs/new-payment-method/new-payment-method.component';
import { SelectPaymentMethodDialogComponent } from '@payment/components/dialogs/select-payment-method-dialog/select-payment-method-dialog.component';
import { addPaymentMethod, removePaymentMethod } from '@payment/state/payment.actions';
import { selectPaymentMethods } from '@payment/state/payment.selectors';
import { State } from '@state/model';
import { PaymentNetworkService } from 'egov-api';
import { Observable, Subject } from 'rxjs';
import { filter, first } from 'rxjs/operators';

@Injectable()
export class PaymentService
{
    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        private readonly scrollableModal: ScrollableModal,
        private readonly dialogNavigations: DialogNavigations,
        private readonly networkService: PaymentNetworkService,
    )
    {}


    // Payment methods

    private readonly paymentMethods$ = os(this.store.select(selectPaymentMethods));


    // New payment method dialog

    public openNewPaymentMethod()
    {
        const dialogRef: MatDialogRef<NewPaymentMethodComponent, string | undefined> =
            this.scrollableModal.open(NewPaymentMethodComponent, { autoFocus: false });

        this.dialogNavigations.set(dialogRef, 'new-payment-method');

        return dialogRef.afterClosed().pipe(first(), filter(method => !!method)) as Observable<string>;
    }


    // Select payment method dialog

    public openSelectPaymentMethod(currentMethod?: string)
    {
        const dialogRef: MatDialogRef<SelectPaymentMethodDialogComponent, string | null | undefined> =
            this.scrollableModal.open(SelectPaymentMethodDialogComponent, { autoFocus: false, data: currentMethod });

        this.dialogNavigations.set(dialogRef, 'select-payment-method');

        return dialogRef.afterClosed().pipe(
            first(),
            filter(method => !!(method || method === null)),
        ) as Observable<string | null>;
    }


    // Add cretid card

    public addCreditCard
    (
        US: boolean, // eslint-disable-line @typescript-eslint/naming-convention
        nameOnCard: string,
        cardNumber: string,
        cardProvider: CardProvider,
        expirationMonth: number,
        expirationYear: number,
        cv2: number,
        address: string,
        zip?: number,
    ):
    Observable<CardPaymentMethod>
    {
        return new Observable<CardPaymentMethod>(subscriber =>
        {
            if (US && !zip)
                throw new Error('A US Card must include ZIP Code information.');

            const timeout: number = window.setTimeout(() =>
            {
                const success: boolean = Math.random() < .95;

                if (success)
                {
                    const newCard: CardPaymentMethod = {
                        id: Math.random() * 999999999 + '',
                        usCard: US,
                        cardProvider,
                        cardLastFour: cardNumber.trim().split(' ').join('').substr(-4),
                    };

                    if (this.paymentMethods$.value)
                    {
                        this.store.dispatch(
                            addPaymentMethod('PaymentService.addCreditCard()', { paymentMethod: newCard })
                        );

                        subscriber.next(newCard);
                        subscriber.complete();
                    }
                }
                else
                    subscriber.error(new Error('Error adding payment method.'));
            },
            3000);

            return { unsubscribe: () => clearTimeout(timeout) };
        });
    }


    // Add echeck

    public addECheck
    (
        nameOnECheck: string,
        accountNumber: number,
        routingNumber: number,
        address: string,
        zip?: number,
    ):
    Observable<EcheckPaymentMethod>
    {
        return new Observable<EcheckPaymentMethod>(subscriber =>
        {
            const timeout: number = window.setTimeout(() =>
            {
                const success: boolean = Math.random() < .95;

                if (success)
                {
                    const newEcheck: EcheckPaymentMethod = {
                        id: Math.random() * 999999999 + '',
                        accountLastFour: (accountNumber + '').trim().split(' ').join('').substr(-4),
                        routingLastFour: (routingNumber + '').trim().split(' ').join('').substr(-4),
                    };

                    if (this.paymentMethods$.value)
                    {
                        this.store.dispatch(
                            addPaymentMethod('PaymentService.addECheck()', { paymentMethod: newEcheck })
                        );

                        subscriber.next(newEcheck);
                        subscriber.complete();
                    }
                }
                else
                    subscriber.error(new Error('Error adding payment method.'));
            },
            3000);

            return { unsubscribe: () => clearTimeout(timeout) };
        });
    }


    // Remove payment method

    public removePaymentMethod(method: PaymentMethod): Subject<void>
    {
        const subject = new Subject<void>();

        window.setTimeout(() =>
        {
            const success: boolean = Math.random() < .95;

            if (success)
            {
                this.store.dispatch(
                    removePaymentMethod('PaymentService.removePaymentMethod()', { id: method.id })
                );

                subject.next();
                subject.complete();
            }
            else
                subject.error(new Error('Error removing payment method.'));
        },
        3000);

        return subject;
    }
}
